import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@/components/catalog/icons';
import { Button } from '@/components/ui';
import type { ModuleSlug } from '@/lib/permissions';

/**
 * One item in a row's create menu.
 *
 * ⚠️ **`href` is RESOLVED BY THE CALLER, and that is the point of the seam.**
 * The two conventions this menu is mounted under are genuinely different and
 * each is documented where it is decided: `/companies` opens a create form with
 * the company locked (`{path}?companyId={id}&new=1`), while `/deals` navigates
 * to the DEAL (`/deals/{id}?create=quote`) because a create form cannot carry a
 * basket — a line is `POST /quotes/{id}/products` and cannot exist before the
 * record does. Moving either rule in here would put a URL convention a page
 * owns somewhere nobody editing that page would look.
 */
export interface RecordCreateMenuItem {
  /**
   * This item's React key.
   *
   * On the two menus gated per module it is the module slug, so that two items
   * can never collide on a rename. ⚠️ **It is typed as a plain `string` because
   * not every item HAS a module**: `ConvertQuoteMenu` gates on the role
   * (`canConvertQuote()`), so its key is a literal. **Do not narrow this to
   * `ModuleSlug`** — that would make a module look mandatory and invite the next
   * caller to gate on `canCreate`, which is the wrong question for convert.
   */
  key: string;
  label: string;
  href: string;
  /**
   * Handle this item HERE instead of navigating.
   *
   * ✅ **THE SEAM EXISTS BECAUSE A URL CANNOT CARRY A BASKET.** Navigating to
   * `?companyId=N&new=1` seeds exactly one thing — the company — so any caller
   * that needs the create form to open with LINES already on it has to mount the
   * form itself. The sales-tracker board is the first: a card is a
   * `(project, company)` pair, and combining several raises one document whose
   * lines each name a different project. `/quotes?…` has nowhere to put that.
   *
   * ⚠️ **RETURN `true` WHEN YOU HANDLED IT; `false` FALLS THROUGH TO `href`.**
   * The boolean is what lets ONE caller intercept SOME of its items rather than
   * all or none.
   *
   * 🔴 **NO LIVE CALLER RETURNS `false` TODAY, and the branch stays.**
   * *SUPERSEDED, the worked example this paragraph carried: "the board takes
   * Quote and Invoice and leaves Deal to navigate, because `DealFormModal` has
   * no line seam to seed and a deal opened with a basket would silently drop
   * every line of it."* The deal form gained `prefill.lines` on 2026-09-13 and
   * the sales tracker took its Deal item on 2026-09-14, so every caller of this
   * seam now intercepts every item. ⛔ **Do NOT "simplify" `onSelect` to
   * `() => void` on the strength of that**: the day a fourth create target
   * appears, or a caller can only handle some kinds, a `void` handler has no way
   * to decline and the item becomes inert — an affordance that does nothing,
   * with nothing failing.
   *
   * ⚠️ **`href` STAYS REQUIRED even on an intercepted item**, deliberately: it is
   * the honest destination if the interception is ever removed, and a menu whose
   * items may have no destination at all is one where forgetting the handler
   * produces a control that does nothing.
   */
  onSelect?: () => boolean;
}

/** Roughly a 200px menu; clamped into the viewport before it is drawn. */
const MENU_WIDTH = 216;

/**
 * The flip estimate, DERIVED from the item count rather than written down per
 * call site.
 *
 * These two numbers reproduce the hand-maintained constants this component was
 * extracted from exactly — 170 for the deals list's two items, 210 for the
 * companies list's three — so the extraction moved no behaviour. It is only
 * ever used to decide whether to open upwards, and it is sized for the tallest
 * case: the rows take the primitives' `max-sm` touch bump, so a phone is the
 * binding case. **Over-estimating flips the menu up slightly early, which is
 * harmless; under-estimating opens it off the bottom of a phone screen.**
 */
const MENU_BASE_HEIGHT = 90;
const MENU_ITEM_HEIGHT = 40;

const MENU_ITEMS = '[role="menuitem"]';

/**
 * The per-row "create something against this record" menu.
 *
 * ONE component, mounted by `/companies`, `/deals` and the two cards on
 * `/university-partnerships` that mirror them. It was extracted from two
 * near-identical copies rather than being copied a third time: two copies agree
 * the day they are written and drift on the first correction, and what would
 * drift here is the wording of a control that starts a real revenue document.
 *
 * ⚠️ **Portalled to the body and positioned `fixed`, and that is not a style
 * choice.** Every mount lives inside a `TableWrap`, whose `overflow-x-auto`
 * computes `overflow-y: auto` as well, so an absolutely-positioned menu would
 * be clipped to the table's box and the lower rows' menus would be unreachable.
 * `z-[60]` is the layer ladder's portalled-popover rung — read the ladder at the
 * top of `src/index.css` before changing it; it is above the overlay rung so
 * that a menu opened from inside a dialog still paints over it. It follows the
 * trigger on scroll and resize.
 *
 * ⚠️ **It navigates; it creates nothing and converts nothing.** No request is
 * made from here. Each destination page re-checks the permission the call site
 * gated on — `ability.canCreate(...)` on the two create menus,
 * `ability.canConvertQuote()` on `ConvertQuoteMenu` — before honouring the
 * parameter it is handed, since a URL can be typed. **The gating at the call
 * site decides what is OFFERED, and is not authorisation.**
 */
export function RecordCreateMenu({
  recordName,
  triggerLabel,
  items,
  prominent = false,
}: {
  /**
   * The record the items are raised against, for the heading and the labels.
   *
   * ⚠️ A NAME, not a record. Mounted from tables, from a board card and from a
   * dashboard row, and a `KanbanCard` carries none of the fifty-odd columns a
   * `Company` has. Widen this only when the menu genuinely needs a second field,
   * and say what every caller is to pass for it.
   */
  recordName: string;
  /**
   * The trigger's accessible name, supplied per call site rather than derived
   * from the item labels.
   *
   * ⚠️ **Never a bare "Add": in a table of twenty rows, twenty buttons called
   * "Add" are twenty identical entries in a screen reader's list.**
   *
   * ⚠️ *SUPERSEDED, the reason it is passed in: "so that the extraction changed
   * no existing page's accessible name — deriving it from a permission-filtered
   * item list would have silently reworded a live control on two pages."* The
   * owner reworded the deals trigger on 2026-09-04, so that constraint is spent.
   * **It stays passed in for the half that outlives it**: composing it from a
   * permission-filtered list makes a control's accessible name depend on the
   * reader's permissions, so two colleagues hear two different buttons and
   * neither can be told which one to press. **Do not start deriving it.**
   */
  triggerLabel: string;
  /**
   * Already filtered by the caller, against whichever permission actually gates
   * each item — `ability.canCreate(module)` on the two create menus, the ROLE
   * (`ability.canConvertQuote()`) on the convert menu. ⚠️ **Not one shared
   * question**: see `RecordCreateMenuItem.key`.
   */
  items: readonly RecordCreateMenuItem[];
  /**
   * A labelled "New" trigger instead of the bare "+" icon, for a record's own
   * page header where the menu is a primary entry point (owner, 2026-10-18).
   * Tables and boards keep the compact icon.
   */
  prominent?: boolean;
}) {
  const navigate = useNavigate();
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState({
    left: 0,
    top: 0,
    width: MENU_WIDTH,
    flip: false,
  });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const estimatedHeight = MENU_BASE_HEIGHT + items.length * MENU_ITEM_HEIGHT;

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    const width = Math.min(MENU_WIDTH, window.innerWidth - 16);
    const below = window.innerHeight - box.bottom;
    const flip = below < estimatedHeight && box.top > below;
    // Right-aligned to the trigger, then clamped: this sits in the last column
    // of a table that may already be wider than the viewport, so a left-aligned
    // menu would hang off a 320px screen.
    const maxLeft = Math.max(8, window.innerWidth - width - 8);
    const left = Math.min(Math.max(box.right - width, 8), maxLeft);
    setRect({ left, top: flip ? box.top : box.bottom, width, flip });
  }, [estimatedHeight]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>(MENU_ITEMS)?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function close(returnFocus = true) {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      // A row menu can be opened from inside other dismissable surfaces; don't
      // let one Escape close two things.
      event.stopPropagation();
      close();
      return;
    }

    // The rendered elements, not the `items` prop: roving focus moves between
    // real DOM nodes, and reading them back is what keeps this correct if an
    // item is ever conditionally rendered.
    const elements = Array.from(menuRef.current?.querySelectorAll<HTMLElement>(MENU_ITEMS) ?? []);
    if (elements.length === 0) return;

    if (event.key === 'Tab') {
      // Portalled, this is nowhere near its trigger in the tab order, so an
      // uncaught Tab would strand focus at the end of the document.
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
      return;
    }

    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const current = elements.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? elements.length - 1
          : event.key === 'ArrowDown'
            ? (current + 1) % elements.length
            : (current - 1 + elements.length) % elements.length;
    elements[next]?.focus();
  }

  /*
   * Below every hook, so the guard cannot change the hook order.
   *
   * Every call site already tests this to decide its own column count, so this
   * is a backstop rather than the gate: a trigger whose only outcome is an empty
   * popover is an affordance with one outcome, which this codebase does not
   * ship.
   */
  if (items.length === 0) return null;

  return (
    <>
      <Button
        ref={triggerRef}
        variant={prominent ? 'secondary' : 'ghost'}
        size={prominent ? undefined : 'sm'}
        iconOnly={!prominent}
        leadingIcon={<PlusIcon />}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={triggerLabel}
        title={`New record for ${recordName}`}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={onTriggerKeyDown}
      >
        {prominent ? 'New' : undefined}
      </Button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            onKeyDown={onMenuKeyDown}
            style={{
              position: 'fixed',
              left: rect.left,
              width: rect.width,
              ...(rect.flip
                ? { bottom: window.innerHeight - rect.top + 4 }
                : { top: rect.top + 4 }),
            }}
            className="z-[60] overflow-hidden rounded-lg border border-border bg-surface-raised p-1 shadow-lg"
          >
            {/* The label sits OUTSIDE `role="menu"`: a menu may only contain
                menu items, and prose inside one is read as an item with no
                role. It names which record the items belong to, which nothing
                else in the portalled popover says. */}
            <p className="truncate px-2 pt-1 pb-1 text-xs font-medium text-fg-muted">
              New for {recordName}
            </p>
            <div role="menu" id={menuId} aria-label={`New record for ${recordName}`}>
              {items.map((item) => (
                <Button
                  key={item.key}
                  role="menuitem"
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setOpen(false);
                    // `?? false` so an item with no handler navigates exactly as
                    // every item always has — the interception is opt-in per
                    // item, and an unhandled one must never become inert.
                    if (item.onSelect?.() ?? false) return;
                    navigate(item.href);
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/* --- the two conventions, one copy of each -------------------------------- */

/**
 * The three records raised *against* a company, in the order the business
 * raises them.
 *
 * ⚠️ Each is gated on ITS OWN module, never on one shared flag: creating a deal
 * and creating an invoice are separate permissions and a set may hold one
 * without the other. A target the user cannot create is DROPPED from the menu
 * rather than shown disabled.
 */
export const COMPANY_CREATE_TARGETS = [
  { module: 'deals', label: 'Deal', path: '/deals' },
  { module: 'quotes', label: 'Quote', path: '/quotes' },
  { module: 'invoices', label: 'Invoice', path: '/invoices' },
] as const satisfies readonly {
  module: ModuleSlug;
  label: string;
  path: string;
}[];

export type CompanyCreateTarget = (typeof COMPANY_CREATE_TARGETS)[number];

/**
 * The per-row "create something for this company" menu.
 *
 * Mounted by `/companies` (table, board card and stacked card) and by the
 * Companies card on `/university-partnerships`. ONE copy, so the module list
 * and the dashboard cannot offer different records or word the control
 * differently.
 *
 * These items open a create form with the company **already chosen and
 * locked**, via `?companyId=<id>&new=1`. `DealFormModal`, `QuoteFormModal` and
 * `InvoiceFormModal` all take a `lockedCompany` prop and derive create mode
 * from `new=1` rather than local state alone.
 *
 * ⚠️ **`new=1` is not authorisation.** Each page re-checks
 * `ability.canCreate(...)` before honouring it, since a URL can be typed. The
 * list stays filtered underneath the form, so dismissing the modal leaves you
 * looking at that company's records rather than everything.
 *
 * ⚠️ **`company` is an id and a name, NOT a `Company`.** Mounted from a table,
 * from a board card and from a dashboard row, and neither a `KanbanCard` nor a
 * `PartnershipRow` carries the fifty-odd columns a `Company` has — only `id` and
 * `name` are read here. Widen this only when the menu genuinely needs a third
 * field, and say what every caller is to pass for it.
 */
export function CreateForCompanyMenu({
  company,
  targets,
  onSelect,
  prominent = false,
}: {
  company: { id: number; name: string };
  /** See {@link RecordCreateMenu}'s `prominent`. */
  prominent?: boolean;
  targets: readonly CompanyCreateTarget[];
  /**
   * Handle a target here instead of navigating — see
   * {@link RecordCreateMenuItem.onSelect}.
   *
   * ⚠️ **RETURN `true` FOR THE TARGETS YOU TOOK AND `false` FOR THE REST.** It
   * is asked PER MODULE precisely so a caller can intercept some and leave
   * others.
   *
   * 🔴 **BOTH LIVE CALLERS NOW TAKE ALL THREE.** *SUPERSEDED: "the sales-tracker
   * board mounts the combine flow for `quotes` and `invoices` and lets `deals`
   * navigate, because `DealFormModal` has no `prefill` and no line seam, so a
   * deal opened from a combined basket would drop every line and every project
   * and still look like it worked."* `DealFormPrefill.lines` landed on
   * 2026-09-13 with the company "+" seeding flow, and the sales tracker was
   * routed through the same seam on 2026-09-14 — so `/companies` and
   * `/sales-tracker` now agree that every item of this menu opens a seeded form.
   * **The per-module question is kept anyway**; see
   * {@link RecordCreateMenuItem.onSelect} for why the declining branch must not
   * be removed because nothing currently uses it.
   *
   * Omitted entirely, every item navigates exactly as it always has.
   */
  onSelect?: (module: CompanyCreateTarget['module']) => boolean;
}) {
  return (
    <RecordCreateMenu
      prominent={prominent}
      recordName={company.name}
      /*
       * Spelled out rather than composed from `targets`. This is a live page's
       * accessible name and the extraction was not the moment to reword it.
       */
      triggerLabel={`New deal, quote or invoice for ${company.name}`}
      items={targets.map((target) => ({
        key: target.module,
        label: target.label,
        href: `${target.path}?companyId=${company.id}&new=1`,
        ...(onSelect === undefined ? {} : { onSelect: () => onSelect(target.module) }),
      }))}
    />
  );
}

/**
 * The two records raised *against* a deal, in the order the business raises
 * them. Gated per module for the same reason as the companies list above.
 *
 * ⚠️ **THE LABELS SAY "CONVERT TO", AND THAT IS NOT A FLOURISH — do not
 * "simplify" them back to the bare nouns.** *SUPERSEDED 2026-09-04, owner's
 * instruction: `'Quote'` and `'Invoice'`.* Both items are CONVERSIONS of this
 * deal: `?create=quote` opens Convert-to-quote (`POST /deals/{id}/convert`) and
 * `?create=invoice` opens Raise-invoice (`POST /deals/{id}/invoices`), each on
 * the deal's own page. The old labels named the DESTINATION correctly and the
 * ACTION wrongly — "Quote" reads as *create a blank quote*, which is precisely
 * the `/quotes?new=1&dealId=N` behaviour the menu below records as reverted
 * because it lost the deal's lines. A label that describes the reverted
 * behaviour is an invitation to restore it.
 *
 * The companies list above keeps its bare nouns, and the asymmetry is the
 * point: those items really do open a blank create form.
 */
export const DEAL_CREATE_TARGETS = [
  { module: 'quotes', label: 'Convert to quote', create: 'quote' },
  { module: 'invoices', label: 'Convert to invoice', create: 'invoice' },
] as const satisfies readonly {
  module: ModuleSlug;
  label: string;
  /**
   * The value of `?create=` on the deal's own page, which is what opens the
   * action there. Spelled exactly as `DealDetailPage` reads it — anything else
   * opens nothing, silently, which is the correct answer to a typed URL and a
   * silent dead end if it is a caller of this menu that is wrong.
   */
  create: string;
}[];

export type DealCreateTarget = (typeof DEAL_CREATE_TARGETS)[number];

/**
 * The per-row "raise something from this deal" menu.
 *
 * Mounted by `/deals` and by the Deals card on `/university-partnerships`.
 *
 * ⚠️ **It navigates; it converts nothing.** The destination is THIS DEAL —
 * `/deals/{id}?create=…` — and the deal's own page opens the surface it already
 * owns: a prefilled `QuoteFormModal` for a quote and a prefilled
 * `InvoiceFormModal` for an invoice. Neither is fired from here, and neither
 * posts anything until Save.
 *
 * ⚠️ **It used to navigate to `/quotes?new=1&dealId=N&companyId=M`, and that
 * landed on a create form that knew nothing about the deal** — `dealId` was a
 * list filter nothing read into the form, so the lines never crossed and only
 * the company was prefilled. **Do not restore it, on this menu or on any new
 * caller**: a create form cannot carry a basket, because a line is
 * `POST /quotes/{id}/products` and cannot exist before the record does. That is
 * also why a dashboard mounting this menu navigates away rather than opening
 * its own modal — the company-locked form it already has is precisely the
 * reverted behaviour.
 *
 * ⚠️ **`create=` is not authorisation.** The deal page re-checks
 * `ability.canCreate(...)` before honouring it, since a URL can be typed, and
 * opens nothing for a value it does not recognise.
 *
 * ⚠️ **`deal` is an id and a name, NOT a `Deal`** — same rule as the companies
 * menu above, and for the same reason: a dashboard row carries neither.
 */
export function CreateFromDealMenu({
  deal,
  targets,
}: {
  deal: { id: number; dealName: string };
  targets: readonly DealCreateTarget[];
}) {
  return (
    <RecordCreateMenu
      recordName={deal.dealName}
      /*
       * Spelled out, not composed from `targets` — see `triggerLabel` on
       * `RecordCreateMenu`. Reworded on the owner's instruction, 2026-09-04
       * (*SUPERSEDED: "New quote or invoice for …"*): every item here converts
       * this deal, so a trigger promising a *new* document named an action the
       * menu does not offer.
       */
      triggerLabel={`Convert ${deal.dealName} to a quote or invoice`}
      items={targets.map((target) => ({
        key: target.module,
        label: target.label,
        href: `/deals/${deal.id}?create=${encodeURIComponent(target.create)}`,
      }))}
    />
  );
}

/**
 * The per-row "convert this quote" menu — ONE item, `Convert to invoice`.
 *
 * Owner's instruction, 2026-09-04: the quote rows' "+" is to offer convert.
 *
 * ⚠️ **The label matches `QuoteDetailPage`'s primary button BYTE FOR BYTE, and
 * that is a constraint rather than a coincidence.** One action must not have two
 * names: somebody who chooses "Convert to invoice" here lands on a page whose
 * own control has to be recognisable as the thing they just pressed. Reword one
 * and reword the other.
 *
 * ⚠️ **It navigates; it converts nothing.** The destination is THIS QUOTE —
 * `/quotes/{id}?create=invoice` — and the quote's own page opens the prefilled
 * `InvoiceFormModal` it already owns. Nothing is posted until Save there, and
 * that page re-checks its own block before opening anything: a staged or
 * already-billed quote strips the parameter and opens nothing, so a URL cannot
 * step past a refusal the button beside it is already showing.
 *
 * 🔴 **THE GATE IS `ability.canConvertQuote()`, NOT `ability.canCreate(...)`,
 * AND IT LIVES IN THE CALLER.** `POST /quotes/{id}/convert` is
 * `requireRole(...WRITE_ROLES)`, so the ROLE decides and `/permissions/me` must
 * not — the seeded `basic-user` set once lacked `quotes.convert` while the route
 * took any write role, which is why `ROLE_AUTHORITATIVE` in `permissions.ts`
 * keeps the permission list out of this decision.
 *
 * ⚠️ **Which is why the item carries NO `module: ModuleSlug`, unlike
 * `COMPANY_CREATE_TARGETS` and `DEAL_CREATE_TARGETS`. The asymmetry is
 * deliberate; do not close it to look symmetric.** A module slug here would
 * invite the next reader to filter this list with `canCreate` the way both
 * neighbours are filtered — which offers Convert to somebody the server refuses,
 * **or** hides it from somebody the server allows, and neither failure reports
 * itself. With one item there is no per-item filter to write at all: the CALLER
 * decides whether to render the menu, on `canConvertQuote()`.
 *
 * The popover still heads itself "New for <quote>", as both menus above do: a
 * conversion does produce a new invoice for this quote, and the item underneath
 * says which action produces it.
 *
 * ⚠️ **`quote` is an id and a NAME, NOT a `Quote`** — same rule as the two menus
 * above and for the same reason. Callers pass the subject; a dashboard row
 * passes its `primary`, which is that subject with the Xero quote number
 * appended when the quote has one.
 */
export function ConvertQuoteMenu({ quote }: { quote: { id: number; name: string } }) {
  return (
    <RecordCreateMenu
      recordName={quote.name}
      triggerLabel={`Convert ${quote.name} to an invoice`}
      items={[
        {
          /*
           * A literal, which `RecordCreateMenuItem.key` has always allowed. The
           * other two menus key on the module slug only because it is already in
           * hand there to gate on; there is no module in play here.
           */
          key: 'convert-invoice',
          label: 'Convert to invoice',
          href: `/quotes/${quote.id}?create=invoice`,
        },
      ]}
    />
  );
}
